/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Main report function for unified analytics reporting</blockquote>
 * 
 * This function provides a unified interface for reporting analytics events.
 * It automatically selects the appropriate service (Console or PostHog) based on
 * the event properties and configuration.
 * 
 * @example
 * ```typescript
 * import { report, BaseEvent } from '@shared/reporting';
 * 
 * const event: BaseEvent = {
 *   id: 'evt-001',
 *   distinctId: 'user-123',
 *   name: 'user:sign_in',
 *   props: { method: 'credentials' }
 * };
 * 
 * const result = await report(event);
 * result.match({
 *   ok: (success) => console.log('Event reported successfully:', success),
 *   err: error => console.error('Failed to report event:', error)
 * });
 * ```
 */

import { BaseEvent } from '../types/BaseEvent';
import { Reporter } from '../classes/Reporter';
import { AppError, AsyncResult, err, ok } from '@shared/utils';
import { validateBaseEvent } from './validateBaseEvent';
import { Logger } from '@shared/logger';

/**
 * @summary
 * <blockquote>Reports an analytics event using the unified Reporter class</blockquote>
 * 
 * This function validates the event and then uses the Reporter class to report it
 * to all configured services based on ResourceConfig settings.
 * 
 * @param event - The event to report
 * @returns AsyncResult<boolean, AppError> indicating success or failure
 */
export async function report(event: BaseEvent): AsyncResult<boolean, AppError> {
  // Validate the event first
  const validationResult = validateBaseEvent(event);

  if (validationResult.isErr) {
    return err(validationResult.getFail());
  }

  const reporter = new Reporter(event);
  // Fire-and-forget: call report() without awaiting
  // Handle errors properly to prevent unhandled promise rejections
  reporter.report()
    .then((result) => {
      // Check if the result is an error and log it
      result.match({
        ok: () => {
          // Success - no action needed for fire-and-forget
        },
        err: (error) => {
          Logger.error('Failed to report event', {
            eventId: event.id,
            eventName: event.name,
            errorCode: error.errorCode,
            errorMessage: error.errorMessage
          }, error as unknown as Error);
        }
      });
    })
    .catch((error) => {
      // Handle unexpected promise rejections
      Logger.error('Unexpected error during fire-and-forget reporting', {
        eventId: event.id,
        eventName: event.name,
        errorMessage: error instanceof Error ? error.message : String(error)
      }, error instanceof Error ? error : new Error(String(error)));
    });
  
  return ok(true);
} 